import { useQuery } from "@apollo/client";
import { Container, Row as ContainerRow, Table } from "@fluidattacks/design";
import type { ColumnDef, Row as TableRow } from "@tanstack/react-table";
import _, { capitalize } from "lodash";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PORTFOLIO_GROUPS_QUERY } from "./queries";

import { useAuthz } from "hooks/use-authz";
import { useTable } from "hooks/use-table";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

interface IPortfolioGroupsProps {
  readonly organizationId: string;
}

interface IGroupData {
  readonly description: string | null;
  readonly name: string;
  readonly id: string;
}

const PortfolioGroups: React.FC<IPortfolioGroupsProps> = ({
  organizationId,
}): JSX.Element => {
  const { t } = useTranslation();
  const { tagName } = useParams() as { tagName: string };
  const { can } = useAuthz();
  const canGetGroups = can("integrates_api_resolvers_tag_groups_resolve");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data } = useQuery(PORTFOLIO_GROUPS_QUERY, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.error("An error occurred loading tag groups", error);
      });
    },
    variables: { canGetGroups, organizationId, tag: tagName },
  });

  const columns: ColumnDef<IGroupData>[] = [
    {
      accessorKey: "name",
      header: t("organization.portfolios.group.headers.groupName"),
    },
    {
      accessorKey: "description",
      header: t("organization.portfolios.group.headers.description"),
    },
  ];

  const tableData = useMemo(
    (): IGroupData[] =>
      data?.tag?.groups
        ?.map(
          ({ name, description }): IGroupData => ({
            description,
            id: name.toLowerCase(),
            name: capitalize(name),
          }),
        )
        .filter((group): boolean =>
          group.name.toLowerCase().includes(search.toLowerCase()),
        ) ?? [],
    [data?.tag?.groups, search],
  );

  const handleRowClick = useCallback(
    (row: TableRow<IGroupData>): void => {
      navigate(`/groups/${row.original.id}/analytics`);
    },
    [navigate],
  );

  const { table } = useTable<IGroupData, object, string>({
    columns,
    data: tableData,
    id: "portfolio-groups-table",
    options: {
      actionsOptions: {
        onSearch: setSearch,
        search,
        searchPlaceholder: t("organization.portfolios.group.searchPlaceholder"),
      },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: {
        getId: (item: IGroupData): string => item.id,
        onClick: handleRowClick,
      },
    },
    total: tableData.length,
  });

  if (_.isUndefined(data) || _.isEmpty(data)) {
    return <div />;
  }

  return (
    <Container px={1.25}>
      <ContainerRow>
        <Table table={table}>
          <Table.Options />
          <Table.Content />
          <Table.Footer />
        </Table>
      </ContainerRow>
    </Container>
  );
};

export { PortfolioGroups };
